import React, { useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonButton,
  IonIcon,
  IonSkeletonText,
  IonChip,
  IonSpinner,
  IonActionSheet,
  IonAlert,
  IonRefresher,
  IonRefresherContent,
  useIonRouter,
  useIonToast,
} from '@ionic/react';
import {
  call,
  locationOutline,
  navigate,
  logoWhatsapp,
  calendar,
  create,
  createOutline,
  trash,
  ellipsisVertical,
  personAdd,
  peopleOutline,
  documentOutline,
  arrowBack,
} from 'ionicons/icons';
import { useParams, useHistory } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import { useFamily } from '@/hooks/useFamilies';
import { deleteFamily } from '@/api/families';
import { useQueryClient } from '@tanstack/react-query';
import { usePermission } from '@/hooks/usePermission';
import { FAMILY_ROLE_LABELS } from '@/utils/labels';
import type { FamilyMember } from '@/types/family';
import VisitsNeedsTabs from '@/components/visits/VisitsNeedsTabs';
import FamilyKindergartenCard from '@/components/families/FamilyKindergartenCard';
import { getFamilyCard, type FamilyDocType } from '@/api/documents';
import { downloadBlob } from '@/utils/download';
import { googleMapsUrl } from '@/utils/address';

const ROLE_ORDER: Record<string, number> = { head: 0, spouse: 1, child: 2 };
const ROLE_COLORS: Record<string, string> = {
  head: '#5e81f4',
  spouse: '#9698d6',
  child: '#7ce7ac',
};

const FamilyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const router = useIonRouter();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { can } = usePermission();
  const isAdmin = can('families.edit');
  const canViewSensitive = can('members.view_sensitive');
  const validId = id && id !== 'new' ? id : undefined;
  const { data: family, isLoading, refetch } = useFamily(validId);
  const [present] = useIonToast();

  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [downloadingCard, setDownloadingCard] = useState<string | null>(null);

  const handleDownloadCard = async (type: FamilyDocType) => {
    if (!id || !family || downloadingCard) return;
    setDownloadingCard(type);
    try {
      const blob = await getFamilyCard(id, type);
      const suffix = type === 'card-simple' ? 'simpla' : 'completa';
      downloadBlob(blob, `fisa_familiei_${suffix}_${family.name}.pdf`);
    } catch {
      present({ message: 'Eroare la generarea documentului.', duration: 3000, color: 'danger', position: 'bottom' });
    } finally {
      setDownloadingCard(null);
    }
  };

  const handleRefresh = async (event: CustomEvent) => {
    await refetch();
    event.detail.complete();
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteFamily(id);
      void queryClient.invalidateQueries({ queryKey: ['families'] });
      present({ message: 'Familie ștearsă.', duration: 2000, color: 'success', position: 'bottom' });
      router.push('/families', 'back');
    } catch {
      present({
        message: 'Eroare la ștergerea familiei.',
        duration: 3000,
        color: 'danger',
        position: 'bottom',
      });
    }
  };

  /** Navigate to add a new member to this family with smart defaults */
  const handleAddMember = () => {
    if (!family) return;
    const members = family.members ?? [];
    const hasHead = members.some((m) => m.family_role === 'head');
    const hasSpouse = members.some((m) => m.family_role === 'spouse');

    // Smart role: if no head → head, no spouse → spouse, else → child
    let nextRole = 'child';
    if (!hasHead) nextRole = 'head';
    else if (!hasSpouse) nextRole = 'spouse';

    // Navigate to member create with pre-filled params
    const params = new URLSearchParams({
      family_id: id,
      last_name: family.name,
      family_role: nextRole,
    });
    if (family.sector_id) params.set('sector_id', family.sector_id);
    router.push(`/members/new?${params.toString()}`);
  };

  if (isLoading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar className="crm-toolbar">
            <IonButtons slot="start">
              <IonButton onClick={() => history.length > 1 ? history.goBack() : router.push('/families', 'back')}>
                <IonIcon icon={arrowBack} slot="icon-only" />
              </IonButton>
            </IonButtons>
            <IonTitle><IonSkeletonText animated style={{ width: '50%' }} /></IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonSkeletonText animated style={{ width: '100%', height: 200, borderRadius: 12 }} />
        </IonContent>
      </IonPage>
    );
  }

  if (!family && validId) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar className="crm-toolbar">
            <IonButtons slot="start">
              <IonButton onClick={() => history.length > 1 ? history.goBack() : router.push('/families', 'back')}>
                <IonIcon icon={arrowBack} slot="icon-only" />
              </IonButton>
            </IonButtons>
            <IonTitle>Familie Negăsită</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <p>Familia solicitată nu a fost găsită.</p>
        </IonContent>
      </IonPage>
    );
  }

  if (!family) {
    return <IonPage><IonContent /></IonPage>;
  }

  const sortedMembers = [...(family.members ?? [])].sort((a, b) => {
    const roleDiff = (ROLE_ORDER[a.family_role ?? 'child'] ?? 9) - (ROLE_ORDER[b.family_role ?? 'child'] ?? 9);
    if (roleDiff !== 0) return roleDiff;
    // Within same role, sort by age (oldest first = earliest birth_date)
    const ageA = a.age ?? 0;
    const ageB = b.age ?? 0;
    return ageB - ageA;
  });
  const addressParts = [family.address];
  if (family.numar) addressParts.push(`Nr. ${family.numar}`);
  if (family.bloc) addressParts.push(`Bl. ${family.bloc}`);
  if (family.scara) addressParts.push(`Sc. ${family.scara}`);
  if (family.etaj) addressParts.push(`Et. ${family.etaj}`);
  if (family.apartament) addressParts.push(`Ap. ${family.apartament}`);
  const fullAddress = [...addressParts, family.city].filter(Boolean).join(', ');
  const headMember = sortedMembers.find((m) => m.family_role === 'head');

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="crm-toolbar">
          <IonButtons slot="start">
              <IonButton onClick={() => history.length > 1 ? history.goBack() : router.push('/families', 'back')}>
                <IonIcon icon={arrowBack} slot="icon-only" />
              </IonButton>
            </IonButtons>
          <IonTitle>Familia {family.name}</IonTitle>
          {isAdmin && (
            <IonButtons slot="end">
              <IonButton onClick={() => setShowActionSheet(true)}>
                <IonIcon icon={ellipsisVertical} />
              </IonButton>
            </IonButtons>
          )}
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px' }}>
          {/* ── Family Header Card ── */}
          <div style={{
            background: '#fff', borderRadius: 14, padding: '24px 28px',
            marginBottom: 20, border: '1px solid #ececf2',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <h2 style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: 22, color: '#1c1d21', margin: '0 0 4px' }}>
                  Familia {family.name}
                </h2>
                {headMember && (
                  <p style={{ fontSize: 14, color: '#8181a5', margin: '0 0 12px' }}>
                    Cap familie: {headMember.full_name}
                  </p>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {family.sector_name && (
                    <IonChip color="primary" style={{ fontSize: 12, height: 28 }}>
                      {family.sector_name}
                    </IonChip>
                  )}
                  {family.has_unbelieving_spouse && (
                    <IonChip color="warning" style={{ fontSize: 12, height: 28 }}>
                      Soț/soție necredincios(ă)
                    </IonChip>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                  <button className="detail-action detail-action--doc" onClick={() => handleDownloadCard('card-simple')} disabled={!!downloadingCard}>
                    {downloadingCard === 'card-simple' ? <IonSpinner name="crescent" style={{ width: 14, height: 14 }} /> : <IonIcon icon={documentOutline} />} Fișa Simplă
                  </button>
                  {canViewSensitive && (
                    <button className="detail-action detail-action--doc" onClick={() => handleDownloadCard('card-complete')} disabled={!!downloadingCard}>
                      {downloadingCard === 'card-complete' ? <IonSpinner name="crescent" style={{ width: 14, height: 14 }} /> : <IonIcon icon={documentOutline} />} Fișa Completă
                    </button>
                  )}
                </div>
              </div>

              <div style={{
                width: 64, height: 64, borderRadius: 16,
                background: '#5e81f4', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: 24,
                flexShrink: 0,
              }}>
                {(family.name[0] ?? '').toUpperCase()}
              </div>
            </div>

            {/* Address + dates */}
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {fullAddress ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <IonIcon icon={locationOutline} style={{ fontSize: 18, color: '#8181a5', flexShrink: 0 }} />
                  <span style={{ fontSize: 14, color: '#475569' }}>{fullAddress}</span>
                  <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                    {isAdmin && (
                      <IonButton fill="clear" size="small" onClick={() => router.push(`/families/${id}/edit`)}>
                        <IonIcon icon={createOutline} />
                      </IonButton>
                    )}
                    <IonButton
                      fill="clear" size="small"
                      href={googleMapsUrl({ latitude: family.latitude, longitude: family.longitude, text: fullAddress })}
                      target="_blank"
                    >
                      <IonIcon icon={navigate} />
                    </IonButton>
                  </div>
                </div>
              ) : isAdmin ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <IonIcon icon={locationOutline} style={{ fontSize: 18, color: '#f59e0b', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#f59e0b', fontWeight: 500 }}>Adresa lipseste</span>
                  <IonButton fill="clear" size="small" onClick={() => router.push(`/families/${id}/edit`)}>
                    <IonIcon icon={createOutline} />
                    <span style={{ fontSize: 12, marginLeft: 4 }}>Adauga</span>
                  </IonButton>
                </div>
              ) : null}
              {family.wedding_date && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <IonIcon icon={calendar} style={{ fontSize: 18, color: '#8181a5', flexShrink: 0 }} />
                  <span style={{ fontSize: 14, color: '#475569' }}>
                    Căsătoriți din {format(parseISO(family.wedding_date), 'd MMMM yyyy', { locale: ro })}
                  </span>
                </div>
              )}

              {/* Location problem banner */}
              {fullAddress && (!family.latitude || !family.longitude || family.needs_geocoding) && (
                <div
                  className="family-loc-banner"
                  onClick={() => {
                    if (family.latitude && family.longitude) {
                      router.push(`/maps?fly=${id}${family.sector_id ? `&sector_id=${family.sector_id}` : ''}`);
                    } else {
                      const reviewParams = new URLSearchParams();
                      if (family.sector_id) reviewParams.set('sector_id', family.sector_id);
                      reviewParams.set('family_id', id);
                      router.push(`/maps/review?${reviewParams.toString()}`);
                    }
                  }}
                >
                  <IonIcon icon={locationOutline} className="family-loc-banner__icon" />
                  <div className="family-loc-banner__text">
                    <strong>
                      {!family.latitude || !family.longitude
                        ? 'Fără coordonate pe hartă'
                        : 'Locația necesită verificare'}
                    </strong>
                    <span>
                      {!family.latitude || !family.longitude
                        ? 'Această familie are adresă dar nu apare pe hartă. Click pentru a rezolva.'
                        : 'Coordonatele acestei familii au confidență scăzută. Click pentru a verifica.'}
                    </span>
                  </div>
                  <span className="family-loc-banner__arrow">&rarr;</span>
                </div>
              )}
            </div>
          </div>

          {/* ── Family Members Card ── */}
          <div style={{
            background: '#fff', borderRadius: 14, padding: '20px 24px',
            marginBottom: 20, border: '1px solid #ececf2',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <IonIcon icon={peopleOutline} style={{ fontSize: 20, color: '#5e81f4' }} />
                <h3 style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 16, color: '#1c1d21', margin: 0 }}>
                  Membrii Familiei ({family.member_count})
                </h3>
              </div>
              {isAdmin && (
                <IonButton
                  fill="outline"
                  size="small"
                  onClick={handleAddMember}
                  style={{ '--border-radius': '8px', fontWeight: 600 }}
                >
                  <IonIcon icon={personAdd} slot="start" />
                  Adaugă Membru
                </IonButton>
              )}
            </div>

            {sortedMembers.length === 0 ? (
              <div style={{
                textAlign: 'center', padding: '32px 16px',
                color: '#8181a5', fontSize: 14,
                background: '#f8fafc', borderRadius: 10,
                border: '2px dashed #ececf2',
              }}>
                <IonIcon icon={peopleOutline} style={{ fontSize: 32, display: 'block', marginBottom: 8, color: '#ececf2' }} />
                Niciun membru asociat acestei familii.
                {isAdmin && (
                  <>
                    <br />
                    <IonButton fill="clear" size="small" onClick={handleAddMember} style={{ marginTop: 8 }}>
                      Adaugă primul membru
                    </IonButton>
                  </>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {sortedMembers.map((member) => (
                  <MemberRow
                    key={member.id}
                    member={member}
                    // Hand the person page a name for its back button, so
                    // stepping into a child (and editing them) still leads
                    // back here instead of dumping you on /members.
                    onOpen={() => history.push(`/members/${member.id}`, {
                      backTo: { path: `/families/${id}`, label: `Familia ${family.name}` },
                    })}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Children at the kindergarten (roster only, no money) ── */}
          {can('kindergarten.view') && <FamilyKindergartenCard familyId={id} />}

          {/* ── Visits & Needs ── */}
          <VisitsNeedsTabs familyId={id} context="family" />

          {/* ── Notes ── */}
          {family.notes && (
            <div style={{
              background: '#fff', borderRadius: 14, padding: '20px 24px',
              marginBottom: 20, border: '1px solid #ececf2',
            }}>
              <h3 style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 16, color: '#1c1d21', margin: '0 0 8px' }}>
                Observații
              </h3>
              <p style={{ fontSize: 14, color: '#475569', margin: 0, lineHeight: 1.6 }}>
                {family.notes}
              </p>
            </div>
          )}
        </div>

        {/* ── Action Sheet ── */}
        <IonActionSheet
          isOpen={showActionSheet}
          onDidDismiss={() => setShowActionSheet(false)}
          header="Acțiuni"
          buttons={[
            {
              text: 'Editează Familia',
              icon: create,
              handler: () => router.push(`/families/${id}/edit`),
            },
            {
              text: 'Adaugă Membru',
              icon: personAdd,
              handler: handleAddMember,
            },
            {
              text: 'Șterge Familia',
              icon: trash,
              role: 'destructive' as const,
              handler: () => setShowDeleteAlert(true),
            },
            { text: 'Anulează', role: 'cancel' as const },
          ]}
        />

        {/* ── Delete Alert ── */}
        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => setShowDeleteAlert(false)}
          header="Confirmare Ștergere"
          message={`Ești sigur că vrei să ștergi familia ${family.name}? Membrii vor rămâne în sistem dar nu vor mai fi asociați unei familii.`}
          buttons={[
            { text: 'Anulează', role: 'cancel' },
            { text: 'Șterge', role: 'destructive', handler: handleDelete },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

/* ── Member Row Sub-component ── */
function MemberRow({ member, onOpen }: { member: FamilyMember; onOpen: () => void }) {
  const roleColor = ROLE_COLORS[member.family_role ?? 'child'] ?? '#8181a5';

  return (
    <div
      onClick={onOpen}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '12px 14px', borderRadius: 10,
        cursor: 'pointer', transition: 'background 0.15s',
        border: '1px solid transparent',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = '#f8fafc';
        (e.currentTarget as HTMLElement).style.borderColor = '#ececf2';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
        (e.currentTarget as HTMLElement).style.borderColor = 'transparent';
      }}
    >
      {/* Avatar */}
      <div style={{
        width: 42, height: 42, borderRadius: '50%',
        background: roleColor, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, fontWeight: 700, flexShrink: 0,
      }}>
        {(member.first_name[0] ?? '').toUpperCase()}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 14, color: '#1c1d21' }}>
          {member.full_name}
        </div>
        <div style={{ fontSize: 12, color: '#8181a5', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{
            background: `${roleColor}15`,
            color: roleColor,
            padding: '1px 8px', borderRadius: 4,
            fontWeight: 600, fontSize: 11,
          }}>
            {FAMILY_ROLE_LABELS[member.family_role ?? ''] ?? 'Membru'}
          </span>
          {member.age != null && <span>{member.age} ani</span>}
          {member.church_status && member.church_status !== 'active' && (
            <span style={{ color: '#ef4444' }}>{member.church_status}</span>
          )}
        </div>
      </div>

      {/* Quick actions */}
      {member.cell_phone && (
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <a
            href={`tel:${member.cell_phone}`}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: '#eef2ff', color: '#5e81f4',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <IonIcon icon={call} style={{ fontSize: 16 }} />
          </a>
          <a
            href={`https://wa.me/${((d) => d.startsWith('0') ? '40' + d.slice(1) : d)(member.cell_phone.replace(/\D/g, ''))}`}
            onClick={(e) => e.stopPropagation()}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: '#ecfdf5', color: '#059669',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <IonIcon icon={logoWhatsapp} style={{ fontSize: 16 }} />
          </a>
        </div>
      )}
    </div>
  );
}

export default FamilyDetailPage;
